/**
 * THE PORTFOLIO'S TREND — reading a run of daily health pulses (0122) into the
 * one thing a single colour cannot say: is this getting better or worse?
 *
 * Everything here is PURE — it takes a board's stored pulses and derives, with no
 * dates of its own beyond the `now` it is handed:
 *   • the roll-up trend line (an arrow + a word): improving, worsening, steady,
 *     "stuck · N weeks", or "New" on a project's first pulse;
 *   • the report/pack history strip (the last N periods as graded cells, a dated
 *     axis, and a plain sentence that does the arithmetic for the reader).
 *
 * The grade a pulse carries is the SAME worst-of-three the live row shows
 * (src/portfolio.ts) — this module never re-derives health, it only reads what
 * was recorded. "On hold" is not a colour: a parked day breaks a run rather than
 * reading as a green streak, exactly as the live portfolio treats it.
 */
import type { Rag } from './portfolio';

/** One stored daily snapshot, as the app reads it back from `portfolio_pulse`. */
export interface Pulse {
  /** The day the snapshot represents, `YYYY-MM-DD` (a UTC day, like a phase target). */
  pulseOn: string;
  /** The worst-of-three overall grade, or null on a day the project was on hold. */
  overallRag: Rag | null;
  scheduleRag: Rag;
  budgetRag: Rag | null;
  blockersRag: Rag;
  onHold: boolean;
}

/** The words the app shows for each grade — the state, never the colour name. */
export const RAG_WORD: Record<Rag | 'hold', string> = {
  green: 'On track',
  amber: 'At risk',
  red: 'Off track',
  hold: 'On hold',
};

/** Worse is higher. On track < At risk < Off track. */
const RANK: Record<Rag, number> = { green: 0, amber: 1, red: 2 };

/** Improving (colour got better) reads up; worsening reads down; unchanged flat. */
export type TrendDir = 'up' | 'down' | 'flat';

/**
 * The roll-up trend, one of:
 *   none  — no pulse recorded yet (the cell shows nothing).
 *   hold  — parked today; a held project shows no trend.
 *   new   — only one graded pulse ever; nothing to compare ("New · first pulse").
 *   trend — a direction against the comparison period, plus how many whole weeks
 *           the CURRENT grade has been held unbroken (for "stuck · N wks").
 */
export type Trend =
  | { kind: 'none' }
  | { kind: 'hold' }
  | { kind: 'new'; to: Rag }
  | { kind: 'trend'; dir: TrendDir; from: Rag; to: Rag; stuckWeeks: number };

const DAY = 86_400_000;

/** `YYYY-MM-DD` → a whole-day number in UTC, so gaps are exact whatever the
 *  machine's timezone. */
function dayNum(iso: string): number {
  return Math.floor(Date.parse(`${iso}T00:00:00Z`) / DAY);
}

/** Pulses oldest-first, defensively copied. */
function ordered(pulses: Pulse[]): Pulse[] {
  return [...pulses].sort((a, b) => dayNum(a.pulseOn) - dayNum(b.pulseOn));
}

/**
 * How many whole weeks the most recent grade has been held unbroken — counting
 * back from the last pulse while the grade stays equal and the project is not
 * parked. A different colour or a held day ends the run. 0 when the run is under
 * a week (so the caller shows "steady", not "stuck · 0 wks").
 */
function stuckWeeksOf(sorted: Pulse[]): number {
  const last = sorted[sorted.length - 1];
  if (!last || last.onHold || last.overallRag == null) return 0;
  const grade = last.overallRag;
  let startDay = dayNum(last.pulseOn);
  for (let i = sorted.length - 2; i >= 0; i--) {
    const p = sorted[i];
    if (p.onHold || p.overallRag !== grade) break;
    startDay = dayNum(p.pulseOn);
  }
  return Math.floor((dayNum(last.pulseOn) - startDay) / 7);
}

/**
 * THE ROLL-UP TREND — "better or worse than LAST WEEK?", the one question the
 * roll-up cell answers. Compares the latest grade against the most recent graded
 * pulse at least `priorDays` old (a week); with none that far back it compares
 * against the oldest, so a direction shows as soon as there are two. `flat` means
 * unchanged from a week ago — the cell then reads "steady" or, once `stuckWeeks`
 * has built up, "stuck · N wks". A single graded pulse reads "new"; a parked
 * latest pulse reads "hold"; no pulses at all reads "none". A held day breaks a
 * run — never a green streak.
 */
export function healthTrend(pulses: Pulse[], opts: { priorDays?: number } = {}): Trend {
  const priorDays = opts.priorDays ?? 7;
  const sorted = ordered(pulses);
  if (sorted.length === 0) return { kind: 'none' };

  const last = sorted[sorted.length - 1];
  if (last.onHold || last.overallRag == null) return { kind: 'hold' };
  const to = last.overallRag;

  const graded = sorted.filter((p) => !p.onHold && p.overallRag != null);
  if (graded.length <= 1) return { kind: 'new', to };

  const lastDay = dayNum(last.pulseOn);
  const priorAt =
    [...graded].reverse().find((p) => lastDay - dayNum(p.pulseOn) >= priorDays) ?? graded[0];
  const from = priorAt.overallRag as Rag;
  const dir: TrendDir = RANK[to] < RANK[from] ? 'up' : RANK[to] > RANK[from] ? 'down' : 'flat';
  return { kind: 'trend', dir, from, to, stuckWeeks: stuckWeeksOf(sorted) };
}

/**
 * THE LAST-CHANGE TREND — the direction of the most recent grade change (the
 * grade before the current unbroken run vs the grade now), for the report head
 * and its sentence, which have room to describe the change however long ago it
 * was ("Slid from At risk to Off track…, held for 2 weeks"). Unlike the roll-up,
 * this stays "from At risk" even when last week was already Off track. `flat`
 * (from = to) means one grade has held all through the recorded history.
 */
export function changeTrend(pulses: Pulse[]): Trend {
  const sorted = ordered(pulses);
  if (sorted.length === 0) return { kind: 'none' };

  const last = sorted[sorted.length - 1];
  if (last.onHold || last.overallRag == null) return { kind: 'hold' };
  const to = last.overallRag;

  const graded = sorted.filter((p) => !p.onHold && p.overallRag != null);
  if (graded.length <= 1) return { kind: 'new', to };

  let idx = graded.length - 1;
  while (idx > 0 && graded[idx - 1].overallRag === to) idx--;
  const stuckWeeks = stuckWeeksOf(sorted);
  if (idx === 0) return { kind: 'trend', dir: 'flat', from: to, to, stuckWeeks };

  const from = graded[idx - 1].overallRag as Rag;
  const dir: TrendDir = RANK[to] < RANK[from] ? 'up' : 'down';
  return { kind: 'trend', dir, from, to, stuckWeeks };
}

/** One cell of the history strip. `none` is a period with no pulse recorded. */
export interface StripCell {
  cls: 'g' | 'a' | 'r' | 'h' | 'none';
  now: boolean;
}

/** The report/pack Trend block: the strip, a label per cell, the arrow, the say. */
export interface StripView {
  cells: StripCell[];
  /** Per-cell axis labels, month shown only when it changes ("30 Jul","6 Aug","13"…). */
  axis: string[];
  trend: Trend;
  /** A plain-English sentence that does the arithmetic, or '' when there is nothing to say. */
  say: string;
  /** How many of the periods actually carry a pulse. */
  filled: number;
}

const CLS_OF: Record<Rag, StripCell['cls']> = { green: 'g', amber: 'a', red: 'r' };
import { MONTHS } from './when';

/** A UTC day number → { d, mon } for axis and sentence dates. */
function dayParts(day: number): { d: number; mon: number } {
  const dt = new Date(day * DAY);
  return { d: dt.getUTCDate(), mon: dt.getUTCMonth() };
}

/**
 * THE HISTORY STRIP — the last `periods` periods of `periodDays` each, ending on
 * the latest pulse's day (weekly for the report, ~monthly for the pack). A
 * period's cell is the LATEST pulse that falls in it: its overall grade, or a
 * dashed "hold" cell on a parked day, or an empty "none" cell when no pulse was
 * recorded that period. The axis dates each period's end; the sentence names the
 * last change and how long the current grade has held.
 */
export function healthStrip(
  pulses: Pulse[],
  opts: { periods?: number; periodDays?: number } = {},
): StripView {
  const periods = opts.periods ?? 8;
  const periodDays = opts.periodDays ?? 7;
  const sorted = ordered(pulses);

  const cells: StripCell[] = [];
  const axis: string[] = [];
  let filled = 0;

  if (sorted.length === 0) {
    for (let i = 0; i < periods; i++) {
      cells.push({ cls: 'none', now: i === periods - 1 });
      axis.push('');
    }
    return { cells, axis, trend: { kind: 'none' }, say: '', filled: 0 };
  }

  const lastDay = dayNum(sorted[sorted.length - 1].pulseOn);
  let prevMon = -1;
  for (let i = 0; i < periods; i++) {
    // Period i runs (endDay - periodDays, endDay]; the last period ends today.
    const endDay = lastDay - (periods - 1 - i) * periodDays;
    const startDay = endDay - periodDays;
    const inPeriod = sorted.filter((p) => {
      const d = dayNum(p.pulseOn);
      return d > startDay && d <= endDay;
    });
    const rep = inPeriod[inPeriod.length - 1];
    const now = i === periods - 1;
    if (!rep) {
      cells.push({ cls: 'none', now });
    } else {
      filled++;
      cells.push({ cls: rep.onHold || rep.overallRag == null ? 'h' : CLS_OF[rep.overallRag], now });
    }
    const { d, mon } = dayParts(endDay);
    axis.push(mon !== prevMon ? `${d} ${MONTHS[mon]}` : `${d}`);
    prevMon = mon;
  }

  return {
    cells,
    axis,
    trend: changeTrend(pulses),
    say: trendSentence(sorted),
    filled,
  };
}

/**
 * THE PLAIN SENTENCE for the report/pack — derived from the real history, so it
 * does the arithmetic the reader would otherwise do by eye. It names the last
 * grade change (worsening "slid", improving "recovered"), the day it happened,
 * how long the current grade has held since, and — when there was a settled run
 * before that change — what the project was before. Empty when there is nothing
 * worth a sentence (no pulses, or a lone first pulse).
 */
export function trendSentence(pulses: Pulse[]): string {
  const sorted = ordered(pulses);
  const graded = sorted.filter((p) => !p.onHold && p.overallRag != null);
  if (graded.length === 0) return '';

  const last = graded[graded.length - 1];
  const cur = last.overallRag as Rag;

  // Walk back to the first pulse of the current unbroken grade run.
  let changeIdx = graded.length - 1;
  while (changeIdx > 0 && graded[changeIdx - 1].overallRag === cur) changeIdx--;

  // No change within the history: it has simply held one grade throughout.
  if (changeIdx === 0) {
    const wk = stuckWeeksOf(sorted);
    if (graded.length === 1) return '';
    return wk >= 1
      ? `Has held ${RAG_WORD[cur]} for ${plural(wk, 'week')}.`
      : `Steady at ${RAG_WORD[cur]}.`;
  }

  const before = graded[changeIdx - 1].overallRag as Rag;
  const changed = graded[changeIdx];
  const { d, mon } = dayParts(dayNum(changed.pulseOn));
  const on = `${d} ${MONTHS[mon]}`;
  const worse = RANK[cur] > RANK[before];
  const verb = worse ? 'Slid' : 'Recovered';
  const heldWeeks = Math.floor((dayNum(last.pulseOn) - dayNum(changed.pulseOn)) / 7);
  const held =
    heldWeeks >= 1 ? `, and has held ${RAG_WORD[cur]} for ${plural(heldWeeks, 'week')}` : '';

  // A settled run before the change (two or more pulses at `before`) earns a
  // closing clause naming what it was.
  let runStart = changeIdx - 1;
  while (runStart > 0 && graded[runStart - 1].overallRag === before) runStart--;
  const priorRun = changeIdx - runStart;
  const prior = priorRun >= 2 ? ` Was ${RAG_WORD[before]} before that.` : '';

  return `${verb} from ${RAG_WORD[before]} to ${RAG_WORD[cur]} on ${on}${held}.${prior}`;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}
